import React, { useEffect, useRef, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import { useChat } from '../../common/ChatContext';
import { resolveProfileImage } from '../../common/utils';
import { timeAgo } from '../problems/discussion/discussionUtils';
import ChatThread from './ChatThread';
import ResizeHandle from '../ResizeHandle';

const MIN_WIDTH = 320;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 380;
const WIDTH_STORAGE_KEY = 'chatDrawerWidth';
const SEARCH_DEBOUNCE_MS = 300;

const ACCENT = '#137274';

const loadStoredWidth = () => {
  try {
    const stored = Number(localStorage.getItem(WIDTH_STORAGE_KEY));
    return stored >= MIN_WIDTH && stored <= MAX_WIDTH ? stored : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
};

const PresenceDot = ({ online }) => (
  <Box
    sx={{
      width: 10,
      height: 10,
      borderRadius: '50%',
      bgcolor: online ? ACCENT : '#9ca3af',
      border: '2px solid',
      borderColor: 'background.paper',
    }}
  />
);

const ChatDrawer = ({ open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const {
    conversations,
    activeConversationId,
    presenceByUserId,
    selectConversation,
    clearActiveConversation,
    openConversationWithUser,
    searchResults,
    searchUsers,
  } = useChat();

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  // Default behavior is a normal dismissible drawer (closes on outside
  // click/backdrop, like any Drawer). Pinning switches it to "stick" —
  // no backdrop, stays open regardless of what else the user clicks.
  const [pinned, setPinned] = useState(false);
  const [width, setWidth] = useState(loadStoredWidth);
  const latestWidthRef = useRef(width);
  const activeConversation = conversations.find((c) => String(c.id) === String(activeConversationId));

  const startResize = (startX) => {
    const startWidth = width;
    // Anchored right: dragging left (negative clientX delta) should grow it.
    const onMove = (e) => {
      const next = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + (startX - e.clientX)));
      latestWidthRef.current = next;
      setWidth(next);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try {
        localStorage.setItem(WIDTH_STORAGE_KEY, String(latestWidthRef.current));
      } catch {
        // Private browsing / storage disabled — width just won't persist.
      }
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const goToProfile = () => {
    if (!activeConversation?.partner?.username) return;
    navigate(`/user/${activeConversation.partner.username}`);
  };

  // Debounced: searching on every keystroke hammers the API while the user
  // is still typing. A pending timer is cleared on each new keystroke, and
  // a stale response (one that resolves after the user has since changed
  // the input again) is dropped instead of overwriting newer results.
  const searchTimeoutRef = useRef(null);
  const latestQueryRef = useRef('');
  useEffect(() => () => clearTimeout(searchTimeoutRef.current), []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    latestQueryRef.current = value;
    clearTimeout(searchTimeoutRef.current);

    if (!value.trim()) {
      setSearching(false);
      searchUsers('');
      return;
    }

    setSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      await searchUsers(value);
      if (latestQueryRef.current === value) setSearching(false);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleBack = () => clearActiveConversation();

  const handleStartWithUser = async (userId) => {
    clearTimeout(searchTimeoutRef.current);
    setQuery('');
    latestQueryRef.current = '';
    await searchUsers('');
    await openConversationWithUser(userId);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Pinned = non-modal: no backdrop, and the full-viewport Modal root
      // (which MUI gives every Drawer, backdrop or not) has pointer-events
      // disabled so clicking anywhere else reaches the page instead of a
      // dead overlay — the Paper re-enables pointer-events so it stays
      // usable. Net effect while pinned: the drawer stays open and the
      // rest of the app stays interactive until the user explicitly closes
      // it. Unpinned is just a normal dismissible Drawer (closes on
      // outside click, like any other).
      ModalProps={
        pinned
          ? { hideBackdrop: true, keepMounted: true, sx: { pointerEvents: 'none' } }
          : { keepMounted: true }
      }
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: width },
          // No position override here — MUI's own `position: fixed; right: 0`
          // for anchor="right" is what pins the drawer to the right edge.
          // `fixed` already works as a positioning context for the
          // absolutely-positioned ResizeHandle below, so overriding to
          // `relative` (as this used to) isn't needed and silently breaks
          // the right anchor, making the drawer render on the left instead.
          display: 'flex',
          flexDirection: 'column',
          pointerEvents: 'auto',
          boxShadow: (theme) => `-8px 0 32px ${alpha('#000', theme.palette.mode === 'dark' ? 0.5 : 0.18)}`,
        },
      }}
    >
      {!isMobile && (
        <ResizeHandle
          direction="horizontal"
          onResizeStart={startResize}
          sx={{ position: 'absolute', left: 0, top: 0, height: '100%', zIndex: 3 }}
        />
      )}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: '1px solid',
          borderColor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.08),
        }}
      >
        {activeConversation ? (
          <IconButton size="small" onClick={handleBack}>
            <ArrowBackRoundedIcon fontSize="small" />
          </IconButton>
        ) : null}
        {activeConversation ? (
          <Tooltip title="View profile">
            <Box
              onClick={goToProfile}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, cursor: 'pointer' }}
            >
              <Avatar
                src={resolveProfileImage(activeConversation.partner?.profileImage)}
                sx={{ width: 32, height: 32 }}
              >
                {activeConversation.partner?.name?.[0]?.toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={700} noWrap>
                  {activeConversation.partner?.name || activeConversation.partner?.username}
                </Typography>
                {(() => {
                  const presence = presenceByUserId[String(activeConversation.partner?.id)];
                  return (
                    <Typography
                      variant="caption"
                      sx={{ color: presence?.online ? ACCENT : 'text.secondary', fontWeight: presence?.online ? 600 : 400 }}
                    >
                      {presence?.online
                        ? 'Online'
                        : presence?.lastSeenAt
                        ? `Last seen ${timeAgo(presence.lastSeenAt)}`
                        : 'Offline'}
                    </Typography>
                  );
                })()}
              </Box>
            </Box>
          </Tooltip>
        ) : (
          <Typography variant="h6" fontWeight={800} sx={{ flex: 1 }}>
            Messages
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title={pinned ? 'Unpin (closes when you click away)' : 'Pin (stay open while you click around)'}>
          <IconButton size="small" onClick={() => setPinned((p) => !p)} color={pinned ? 'primary' : 'default'}>
            {pinned ? <PushPinRoundedIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onClose}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      {activeConversation ? (
        <ChatThread conversation={activeConversation} />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5 }}>
            <Tooltip title="Messages are protected with industry-standard encryption before being stored.">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 5,
                  bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#000', 0.04),
                  color: 'text.secondary',
                  cursor: 'default',
                }}
              >
                <LockRoundedIcon sx={{ fontSize: 13 }} />
                <Typography variant="caption">Messages are encrypted</Typography>
              </Box>
            </Tooltip>
          </Box>
          <Box sx={{ px: 2, py: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, username, or email"
              value={query}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searching ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>

          {query.trim() ? (
            <List sx={{ overflowY: 'auto' }}>
              {searchResults.length === 0 && !searching && (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                  No users found.
                </Typography>
              )}
              {searchResults.map((user) => (
                <ListItemButton key={user._id} onClick={() => handleStartWithUser(user._id)}>
                  <ListItemAvatar>
                    <Avatar src={resolveProfileImage(user.profileImage)}>
                      {user.name?.[0]?.toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={user.name || user.username} secondary={`@${user.username}`} />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <List sx={{ overflowY: 'auto', flex: 1 }}>
              {conversations.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
                  No conversations yet. Search for someone above to say hi.
                </Typography>
              )}
              {conversations.map((conversation) => (
                <ListItemButton
                  key={conversation.id}
                  onClick={() => selectConversation(conversation.id)}
                >
                  <ListItemAvatar>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      badgeContent={
                        <PresenceDot online={Boolean(presenceByUserId[String(conversation.partner?.id)]?.online)} />
                      }
                    >
                      <Avatar src={resolveProfileImage(conversation.partner?.profileImage)}>
                        {conversation.partner?.name?.[0]?.toUpperCase()}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography fontWeight={conversation.unreadCount ? 800 : 500} noWrap>
                        {conversation.partner?.name || conversation.partner?.username || 'Unknown user'}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {conversation.lastMessage || 'Say hi 👋'}
                      </Typography>
                    }
                  />
                  {conversation.unreadCount > 0 && (
                    <Badge badgeContent={conversation.unreadCount} color="error" max={99} />
                  )}
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      )}
    </Drawer>
  );
};

export default ChatDrawer;
